// src/FlightRadarPage.js - Complete Awesome Redesign
// Topographical map + real-time animated aircraft + rich data table + type-matched icons + 100mi radius
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Custom icons - crisp inline SVGs for each aircraft type (rotates with heading)
import houseIconUrl from './assets/house-icon.png';

// Fix Leaflet default icon paths
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const NMR_LAT = 35.248242;
const NMR_LON = -83.695590;
const RADIUS_KM = 95; // ~55-60 miles (matches current backend limits)

// Type labels for UI
const typeLabels = {
  1: 'Single-Engine',
  2: 'Single-Engine',
  3: 'Multi-Engine',
  4: 'Multi-Engine',
  5: 'Commercial Jet',
  6: 'Military',
  7: 'Military',
  8: 'Helicopter',
  9: 'Military',
  14: 'Drone/UAV',
};

// High-quality SVG airplane icons (crisp, rotatable, type-specific)
function getPlaneIcon(aircraft) {
  const { category, metadata = {}, callsign = '', alt = 0, vel = 0 } = aircraft;
  const model = (metadata.model || '').toLowerCase();
  const isCommercial = callsign.match(/^[A-Z]{3}\d/) || alt > 6000 || vel > 200 || model.includes('boeing') || model.includes('airbus');

  let svg = '';
  let size = 38;
  let typeLabel = typeLabels[category] || 'Aircraft';

  const commonStyle = 'stroke:#111827; stroke-width:1.5; stroke-linejoin:round;';

  if (category === 8 || model.includes('helicopter') || model.includes('rotor')) {
    // Helicopter - orange body + rotor
    svg = `
      <g>
        <rect x="14" y="16" width="10" height="8" rx="1" fill="#f97316" ${commonStyle}/>
        <path d="M9 20 L29 20" fill="none" stroke="#111827" stroke-width="2"/>
        <line x1="19" y1="12" x2="19" y2="4" stroke="#111827" stroke-width="2"/>
        <line x1="12" y1="4" x2="26" y2="4" stroke="#111827" stroke-width="2.5"/>
        <circle cx="19" cy="20" r="2" fill="#111827"/>
      </g>
    `;
    size = 38;
    typeLabel = 'Helicopter';
  } else if (category === 14 || model.includes('drone')) {
    // Drone (quadcopter style) - purple
    svg = `
      <g>
        <circle cx="10" cy="10" r="3.5" fill="#a855f7" ${commonStyle}/>
        <circle cx="28" cy="10" r="3.5" fill="#a855f7" ${commonStyle}/>
        <circle cx="10" cy="28" r="3.5" fill="#a855f7" ${commonStyle}/>
        <circle cx="28" cy="28" r="3.5" fill="#a855f7" ${commonStyle}/>
        <rect x="13" y="13" width="12" height="12" rx="2" fill="#c084fc" ${commonStyle}/>
      </g>
    `;
    size = 36;
    typeLabel = 'Drone';
  } else if (metadata.military || model.includes('fighter') || model.includes('f-16') || model.includes('f-18')) {
    // Military fighter jet - sharp delta wings, red
    svg = `
      <g>
        <polygon points="19,6 27,18 19,30 11,18" fill="#ef4444" ${commonStyle}/>
        <rect x="17" y="14" width="4" height="12" fill="#b91c1c"/>
        <polygon points="11,18 6,16 6,20" fill="#ef4444" ${commonStyle}/>
        <polygon points="27,18 32,16 32,20" fill="#ef4444" ${commonStyle}/>
      </g>
    `;
    size = 40;
    typeLabel = 'Military';
  } else if (isCommercial || category >= 5) {
    // Commercial jet - blue, long fuselage, swept wings
    svg = `
      <g>
        <rect x="8" y="16" width="22" height="8" rx="2" fill="#3b82f6" ${commonStyle}/>
        <polygon points="19,16 9,9 9,16" fill="#2563eb" ${commonStyle}/>
        <polygon points="19,24 9,31 9,24" fill="#2563eb" ${commonStyle}/>
        <polygon points="19,16 29,9 29,16" fill="#2563eb" ${commonStyle}/>
        <polygon points="19,24 29,31 29,24" fill="#2563eb" ${commonStyle}/>
        <rect x="27" y="17" width="6" height="6" rx="1" fill="#1e40af"/>
      </g>
    `;
    size = 44;
    typeLabel = 'Commercial';
  } else if (category === 3 || model.includes('twin') || model.includes('multi')) {
    // Multi-engine (twin prop style) - teal
    svg = `
      <g>
        <rect x="10" y="16" width="18" height="7" rx="1.5" fill="#14b8a6" ${commonStyle}/>
        <polygon points="19,16 11,10 11,16" fill="#0f766e" ${commonStyle}/>
        <polygon points="19,23 11,29 11,23" fill="#0f766e" ${commonStyle}/>
        <circle cx="12" cy="19.5" r="2.5" fill="#134e4b"/>
        <circle cx="25" cy="19.5" r="2.5" fill="#134e4b"/>
      </g>
    `;
    size = 38;
    typeLabel = 'Multi-Engine';
  } else {
    // Private / single engine - nice green
    svg = `
      <g>
        <rect x="11" y="16" width="16" height="7" rx="1.5" fill="#22c55e" ${commonStyle}/>
        <polygon points="19,16 11,11 11,16" fill="#15803d" ${commonStyle}/>
        <polygon points="19,23 11,28 11,23" fill="#15803d" ${commonStyle}/>
        <circle cx="24" cy="19.5" r="2" fill="#166534"/>
      </g>
    `;
    size = 36;
    typeLabel = 'Private';
  }

  const iconHtml = `
    <svg width="${size}" height="${size}" viewBox="0 0 38 38" style="transform: rotate(${aircraft.heading || 0}deg); transition: transform 0.6s ease-out; filter: drop-shadow(0 1.5px 2.5px rgba(0,0,0,0.35));">
      ${svg}
    </svg>
  `;

  return { html: iconHtml, size, typeLabel };
}

// Haversine distance in km
function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function FlightRadarPage({ isMobile }) {
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const animationFrameRef = useRef(null);
  const previousDataRef = useRef({});

  const [aircraftData, setAircraftData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [filterType, setFilterType] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIcao, setHighlightedIcao] = useState(null);

  const functions = getFunctions();
  const getFlightDataCallable = httpsCallable(functions, 'getFlightData');
  const getPlaneImageCallable = httpsCallable(functions, 'getPlaneImage');

  const fallbackImage = 'https://placehold.co/280x160/1f2937/9ca3af?text=No+Photo&font=roboto';

  // === FETCH FLIGHT DATA (ADS-B Exchange) ===
  const fetchFlights = useCallback(async (isAuto = false) => {
    if (loading) return;
    setLoading(true);
    setError(null);

    try {
      const result = await getFlightDataCallable();
      const rawStates = result.data?.states || [];

      const processed = rawStates
        .map((state) => {
          const icao = state[0];
          const callsign = (state[1] || '').trim() || 'N/A';
          const lon = state[5];
          const lat = state[6];
          const baroAlt = state[7];
          const onGround = state[8];
          const vel = state[9] || 0;
          const heading = state[10] || 0;
          const geoAlt = state[13];
          let category = state[17] || 0;
          const metadata = state[18] || {};

          if (lat == null || lon == null || onGround) return null;

          const alt = Math.round(geoAlt || baroAlt || 0);
          const distance = calculateDistance(NMR_LAT, NMR_LON, lat, lon);

          if (distance > RADIUS_KM + 10) return null;

          // Improve category inference
          if (category === 0 && metadata.model) {
            const m = metadata.model.toLowerCase();
            if (m.includes('helicopter') || m.includes('rotor')) category = 8;
            else if (m.includes('drone')) category = 14;
            else if (m.includes('boeing') || m.includes('airbus') || m.includes('a320') || m.includes('b737')) category = 5;
            else if (metadata.military) category = 6;
            else if (m.includes('cessna') || m.includes('piper') || m.includes('beech')) category = 1;
            else category = 3;
          }

          return {
            icao,
            callsign,
            lat,
            lon,
            alt,
            vel: Math.round(vel),
            heading: Math.round(heading),
            category,
            metadata,
            distance: Math.round(distance * 10) / 10,
          };
        })
        .filter(Boolean)
        .sort((a, b) => a.distance - b.distance);

      setAircraftData(processed);
      setLastUpdated(new Date());
      previousDataRef.current = Object.fromEntries(processed.map(p => [p.icao, p]));
    } catch (err) {
      console.error('Flight fetch error:', err);
      console.error('Flight data error (full):', err);
      const msg = err?.message || err?.details || 'Failed to load live flight data';
      const code = err?.code ? ` [${err.code}]` : '';
      setError(`${msg}${code}`);
    } finally {
      setLoading(false);
    }
  }, [getFlightDataCallable]);

  // Lazy load aircraft photo only when needed
  const loadAircraftPhoto = useCallback(async (aircraft) => {
    const { icao, metadata } = aircraft;
    const query = metadata.registration
      ? `${metadata.registration} ${metadata.model || ''}`
      : `${metadata.manufacturerName || ''} ${metadata.model || ''} ${icao}`;

    try {
      const res = await getPlaneImageCallable({ icao: icao }); // try ICAO first
      if (res.data?.image) return res.data.image;
    } catch (_) {}

    // fallback to search query version if backend supports it
    try {
      const res2 = await getPlaneImageCallable({ query });
      return res2.data?.image || fallbackImage;
    } catch (_) {
      return fallbackImage;
    }
  }, [getPlaneImageCallable]);

  // === MAP INITIALIZATION (Topo + Community + Radius Circle) ===
  useEffect(() => {
    if (mapRef.current) return;

    const map = L.map('flight-map', {
      center: [NMR_LAT, NMR_LON],
      zoom: 8,
      minZoom: 6,
      maxZoom: 16,
    });

    // === TOPOGRAPHICAL MAP - OpenTopoMap (perfect for mountains) ===
    L.tileLayer('https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenTopoMap (CC-BY-SA) | Map data © OpenStreetMap contributors',
      maxZoom: 17,
    }).addTo(map);

    // Community marker (Nantahala Mountain Resort)
    const communityIcon = L.divIcon({
      className: 'community-marker',
      html: `
        <div style="background:#0ea5e9; border:3px solid white; box-shadow:0 0 0 3px rgba(14,165,233,0.3); width:28px; height:28px; border-radius:50%; display:flex; align-items:center; justify-content:center;">
          <div style="width:12px; height:12px; background:white; border-radius:50%;"></div>
        </div>
        <div style="position:absolute; top:30px; left:50%; transform:translateX(-50%); background:#0f172a; color:#e0f2fe; font-size:10px; padding:1px 6px; border-radius:3px; white-space:nowrap; font-weight:600; box-shadow:0 1px 3px rgba(0,0,0,0.4);">NMR</div>
      `,
      iconSize: [28, 28],
      iconAnchor: [14, 14],
    });

    L.marker([NMR_LAT, NMR_LON], { icon: communityIcon })
      .addTo(map)
      .bindPopup('<b>Nantahala Mountain Resort</b><br>Community Center');

    // 100-mile radius circle (beautiful dashed style)
    const radiusCircle = L.circle([NMR_LAT, NMR_LON], {
      radius: RADIUS_KM * 1000,
      color: '#0ea5e9',
      weight: 2,
      opacity: 0.85,
      fillColor: '#0ea5e9',
      fillOpacity: 0.06,
      dashArray: '6, 4',
    }).addTo(map);

    mapRef.current = map;

    // Initial data load
    fetchFlights();

    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // === SMOOTH ANIMATED MARKER UPDATES ===
  const updateMarkers = useCallback((aircraftList) => {
    if (!mapRef.current) return;

    const map = mapRef.current;
    const now = Date.now();

    aircraftList.forEach((ac) => {
      const { icao, lat, lon, heading, callsign, alt, vel, category, metadata, distance } = ac;
      const iconInfo = getPlaneIcon(ac);
      const size = iconInfo.size;

      const prev = previousDataRef.current[icao] || { lat, lon };

      // Create or update marker
      let marker = markersRef.current[icao];

      const divIcon = L.divIcon({
        className: `aircraft-marker ${highlightedIcao === icao ? 'highlighted' : ''}`,
        html: iconInfo.html,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
        popupAnchor: [0, -size / 2 - 4],
      });

      if (!marker) {
        marker = L.marker([lat, lon], { icon: divIcon, zIndexOffset: 1000 })
          .addTo(map)
          .on('click', () => {
            setHighlightedIcao(icao);
            map.flyTo([lat, lon], Math.max(map.getZoom(), 10), { duration: 0.8 });
            showPopup(marker, ac);
          });

        markersRef.current[icao] = marker;
      } else {
        // Smoothly animate to new position
        const startLat = prev.lat;
        const startLon = prev.lon;
        const duration = 850;
        const startTime = now;

        const animate = (timestamp) => {
          const progress = Math.min((timestamp - startTime) / duration, 1);
          const ease = 1 - Math.pow(1 - progress, 3); // easeOutCubic

          const currentLat = startLat + (lat - startLat) * ease;
          const currentLon = startLon + (lon - startLon) * ease;

          marker.setLatLng([currentLat, currentLon]);

          if (progress < 1) {
            requestAnimationFrame(animate);
          } else {
            marker.setLatLng([lat, lon]);
          }
        };

        requestAnimationFrame(animate);
        marker.setIcon(divIcon);
      }

      // Update popup content (rich info)
      const popupHtml = `
        <div style="min-width:240px; font-family:system-ui;">
          <div style="font-weight:700; font-size:15px; margin-bottom:4px;">${callsign}</div>
          <div style="display:grid; grid-template-columns: auto 1fr; gap:2px 10px; font-size:12.5px;">
            <span style="color:#64748b;">Altitude</span><span><strong>${alt.toLocaleString()}</strong> m</span>
            <span style="color:#64748b;">Speed</span><span><strong>${vel}</strong> kts</span>
            <span style="color:#64748b;">Heading</span><span><strong>${heading}°</strong></span>
            <span style="color:#64748b;">Distance</span><span><strong>${distance}</strong> km from NMR</span>
            <span style="color:#64748b;">Type</span><span>${getPlaneIcon(ac).typeLabel}</span>
            <span style="color:#64748b;">Reg</span><span>${metadata.registration || '—'}</span>
            <span style="color:#64748b;">Model</span><span>${metadata.model || '—'}</span>
          </div>
          <div style="margin-top:8px; font-size:11px; color:#64748b;">ICAO: ${icao}</div>
        </div>
      `;

      marker.bindPopup(popupHtml, { closeButton: true, minWidth: 240 });
    });

    // Remove markers for aircraft no longer in view
    Object.keys(markersRef.current).forEach((key) => {
      if (!aircraftList.find((a) => a.icao === key)) {
        map.removeLayer(markersRef.current[key]);
        delete markersRef.current[key];
      }
    });
  }, [highlightedIcao]);

  const showPopup = (marker, ac) => {
    marker.openPopup();
  };

  // React to data changes → update map markers with animation
  useEffect(() => {
    updateMarkers(aircraftData);
  }, [aircraftData, updateMarkers]);

  // Auto-refresh every 42 seconds
  useEffect(() => {
    let interval = null;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchFlights(true);
      }, 42000);
    }
    return () => interval && clearInterval(interval);
  }, [autoRefresh, fetchFlights]);

  // === FILTERED + SEARCHED DATA ===
  const filteredData = useMemo(() => {
    return aircraftData
      .filter((ac) => {
        if (filterType !== 'All') {
          const t = getPlaneIcon(ac).typeLabel;
          if (filterType === 'Commercial' && !t.includes('Commercial')) return false;
          if (filterType === 'Private' && !t.includes('Engine')) return false;
          if (filterType === 'Helicopter' && t !== 'Helicopter') return false;
          if (filterType === 'Military' && t !== 'Military') return false;
          if (filterType === 'Drone' && t !== 'Drone/UAV') return false;
        }
        if (searchTerm) {
          const term = searchTerm.toLowerCase();
          return (
            ac.callsign.toLowerCase().includes(term) ||
            (ac.metadata.registration || '').toLowerCase().includes(term) ||
            (ac.metadata.model || '').toLowerCase().includes(term)
          );
        }
        return true;
      });
  }, [aircraftData, filterType, searchTerm]);

  // Chart data (by type)
  const chartData = useMemo(() => {
    const counts = {};
    aircraftData.forEach((ac) => {
      const label = getPlaneIcon(ac).typeLabel;
      counts[label] = (counts[label] || 0) + 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [aircraftData]);

  const COLORS = ['#0ea5e9', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6'];

  const handleRowClick = (ac) => {
    setHighlightedIcao(ac.icao);
    const marker = markersRef.current[ac.icao];
    if (marker && mapRef.current) {
      mapRef.current.flyTo([ac.lat, ac.lon], 10, { duration: 1.1 });
      marker.openPopup();
    }
  };

  const clearHighlight = () => setHighlightedIcao(null);

  // Manual refresh
  const handleRefresh = () => fetchFlights(false);

  return (
    <div className="flight-radar-page p-4 md:p-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Live Airspace • Nantahala</h1>
          <p className="text-slate-600 text-sm mt-0.5">Real-time aircraft near the resort • ADS-B Exchange • Topographical view</p>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm">
            <div className={`w-2.5 h-2.5 rounded-full ${loading ? 'bg-amber-500 animate-pulse' : 'bg-emerald-500'}`}></div>
            <span className="text-slate-600 font-medium">
              {lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Loading...'}
            </span>
          </div>

          <button
            onClick={handleRefresh}
            disabled={loading}
            className="px-4 py-2 rounded-lg bg-slate-900 hover:bg-black text-white text-sm font-medium disabled:opacity-60 transition"
          >
            {loading ? 'Updating...' : 'Refresh Now'}
          </button>

          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`px-3 py-2 rounded-lg text-sm font-medium border transition ${autoRefresh ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-white border-slate-200'}`}
          >
            {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-4 text-sm flex items-start gap-3">
          <div className="flex-1">
            <div className="font-semibold">Unable to load flight data</div>
            <div className="mt-0.5">{error}</div>
            <div className="text-xs mt-1 text-red-600/70">Open browser DevTools → Console for the full technical error. This is usually caused by temporary API rate limits.</div>
          </div>
          <button 
            onClick={() => { setError(null); fetchFlights(false); }} 
            className="px-3 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded text-xs font-medium whitespace-nowrap"
          >
            Try Again
          </button>
        </div>
      )}

      {/* MAP */}
      <div className="relative rounded-2xl overflow-hidden shadow-xl border border-slate-200 mb-6" style={{ height: isMobile ? '420px' : '560px' }}>
        <div id="flight-map" className="w-full h-full" />
        
        {/* Floating legend */}
        <div className="absolute top-4 right-4 bg-white/95 backdrop-blur px-3 py-2 rounded-xl shadow text-xs border border-slate-200">
          <div className="font-semibold mb-1.5 text-slate-700">Aircraft Types</div>
          <div className="space-y-1 text-[11px]">
            <div className="flex items-center gap-2"><span className="inline-block w-3 h-3 bg-[#0ea5e9] rounded" /> Commercial</div>
            <div className="flex items-center gap-2"><span className="inline-block w-3 h-3 bg-[#22c55e] rounded" /> Private</div>
            <div className="flex items-center gap-2"><span className="inline-block w-3 h-3 bg-[#f59e0b] rounded" /> Helicopter</div>
            <div className="flex items-center gap-2"><span className="inline-block w-3 h-3 bg-[#ef4444] rounded" /> Military</div>
          </div>
        </div>

        <div className="absolute bottom-3 left-3 bg-white/90 text-[10px] px-2 py-0.5 rounded font-mono text-slate-500 border border-slate-200">
          {aircraftData.length} aircraft detected (within current limits)
        </div>
      </div>

      {/* STATS + CHART + FILTERS */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mb-4">
        {/* Stats */}
        <div className="lg:col-span-5 bg-white rounded-2xl border border-slate-200 p-4">
          <div className="flex justify-between items-center mb-3">
            <div className="font-semibold text-slate-800">Airspace Summary</div>
            <div className="text-xs text-slate-500">~{RADIUS_KM} km (~50 NM) radius — ADS-B Exchange</div>
          </div>
          <div className="text-5xl font-semibold tabular-nums tracking-tighter text-slate-900 mb-1">{aircraftData.length}</div>
          <div className="text-slate-600 text-sm">aircraft currently airborne</div>
        </div>

        {/* Type Distribution Chart */}
        <div className="lg:col-span-7 bg-white rounded-2xl border border-slate-200 p-4">
          <div className="font-semibold text-slate-800 mb-2 text-sm">Aircraft Type Distribution</div>
          {chartData.length > 0 ? (
            <div style={{ height: 148 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie dataKey="value" data={chartData} cx="50%" cy="48%" innerRadius={42} outerRadius={68} paddingAngle={2}>
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend verticalAlign="middle" align="right" layout="vertical" iconType="circle" />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-[148px] flex items-center justify-center text-slate-400 text-sm">No aircraft data yet</div>
          )}
        </div>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {['All', 'Commercial', 'Private', 'Helicopter', 'Military', 'Drone'].map((t) => (
          <button
            key={t}
            onClick={() => setFilterType(t)}
            className={`px-3 py-1 text-sm rounded-full border transition ${filterType === t ? 'bg-slate-900 text-white border-slate-900' : 'bg-white hover:bg-slate-50 border-slate-200'}`}
          >
            {t}
          </button>
        ))}

        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search callsign, tail, or model..."
          className="ml-auto border border-slate-200 focus:border-slate-400 rounded-lg px-3 py-1.5 text-sm w-64"
        />
        <button onClick={() => { setFilterType('All'); setSearchTerm(''); clearHighlight(); }} className="text-sm text-slate-500 hover:text-slate-700">Clear</button>
      </div>

      {/* DATA TABLE */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-600">
                <th className="py-3 px-4 font-medium">Aircraft</th>
                <th className="py-3 px-4 font-medium">Flight / Callsign</th>
                <th className="py-3 px-4 font-medium">Tail</th>
                <th className="py-3 px-4 font-medium text-right">Altitude</th>
                <th className="py-3 px-4 font-medium text-right">Speed</th>
                <th className="py-3 px-4 font-medium text-right">Distance</th>
                <th className="py-3 px-4 font-medium">Type</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredData.length === 0 && (
                <tr><td colSpan={7} className="py-8 text-center text-slate-500">No matching aircraft in the current filter.</td></tr>
              )}
              {filteredData.map((ac) => {
                const iconInfo = getPlaneIcon(ac);
                const isHighlighted = highlightedIcao === ac.icao;
                return (
                  <tr
                    key={ac.icao}
                    onClick={() => handleRowClick(ac)}
                    className={`group cursor-pointer hover:bg-slate-50 transition ${isHighlighted ? 'bg-sky-50' : ''}`}
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div style={{ width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center' }} dangerouslySetInnerHTML={{ __html: iconInfo.html.replace(/width="\d+"/, 'width="26"').replace(/height="\d+"/, 'height="26"') }} />
                        <div>
                          <div className="font-medium">{ac.metadata.model || 'Unknown Aircraft'}</div>
                          <div className="text-xs text-slate-500">{iconInfo.typeLabel}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 font-semibold text-slate-800">{ac.callsign}</td>
                    <td className="py-3 px-4 font-mono text-xs text-slate-600">{ac.metadata.registration || '—'}</td>
                    <td className="py-3 px-4 text-right font-medium tabular-nums">{ac.alt.toLocaleString()} m</td>
                    <td className="py-3 px-4 text-right tabular-nums">{ac.vel} kts</td>
                    <td className="py-3 px-4 text-right font-medium text-sky-700">{ac.distance} km</td>
                    <td className="py-3 px-4">
                      <span className="inline-block px-2.5 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
                        {iconInfo.typeLabel}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 text-xs text-slate-500 border-t bg-slate-50">
          Click any row to fly the map to that aircraft. Data from ADS-B Exchange.
        </div>
      </div>

      <div className="text-[10px] text-center text-slate-400 mt-6">Aircraft icons rotate with real heading • Updates every ~42s when auto-refresh is enabled</div>
    </div>
  );
}

export default FlightRadarPage;